import { defaultTo, each } from 'lodash';
import gsap from 'gsap';
import { Application, utils, Ticker, UPDATE_PRIORITY } from './pixi';
import { Component } from './components/shared/Component';
import { finishLoading, setFullscreen, triggerEvent, registerEventListener } from './utility/Utility';
import { showDevtoolsPanel } from './utility/Devtools';

export class App {
  constructor({ canvasId, resizeTo, backgroundColor, backgroundAlpha, masterTicker } = {}) {
    this.canvasId = defaultTo(canvasId, 'app');
    this.canvas = document.getElementById(this.canvasId);
    this.resizeTo = defaultTo(resizeTo, document.documentElement);
    this.backgroundColor = defaultTo(backgroundColor, 0x000000);
    this.backgroundAlpha = defaultTo(backgroundAlpha, 1);
    this.masterTicker = defaultTo(masterTicker, null);
    this.components = {};

    this.app = new Application({
      view: this.canvas,
      resizeTo: this.resizeTo,
      width: this.canvas.clientWidth,
      height: this.canvas.clientHeight,
      antialias: false,
      backgroundColor: this.backgroundColor,
      backgroundAlpha: this.backgroundAlpha,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
      sharedTicker: this.masterTicker === 'pixi',
    });

    this.setMasterTicker();

    if (process.env.NODE_ENV === 'development') {
      // Enables PixiJS Chrome extension
      globalThis.__PIXI_APP__ = this.app; // eslint-disable-line
    }

    showDevtoolsPanel(this.app);

    this.app.stage.eventMode = 'static';
    this.app.stage.on('tap', setFullscreen);
    this.app.stage.on('pointertap', this.notifyTap);

    if (window.integration) {
      registerEventListener('Integration.ToggleUIButtons', (event) => {
        if (event.detail.value) {
          this.canvas.style.pointerEvents = 'auto';
        } else {
          this.canvas.style.pointerEvents = 'none';
        }
      }, 'app');
    }
  }

  get size() {
    return {
      width: this.app.screen.width,
      height: this.app.screen.height,
    };
  }

  get stage() {
    return this.app.stage;
  }

  get view() {
    return this.app.view;
  }

  add(component) {
    if (component instanceof Component) {
      this.app.stage.addChild(component.container);
      this.components[component.name] = component;
    }
  }

  addOnly(component) {
    this.clear();
    this.add(component);

    finishLoading();
  }

  clear() {
    this.app.stage.removeChildren();
  }

  clearCache() {
    utils.clearTextureCache();
  }

  mute(isSoundOff) {
    each(this.components, (component) => {
      if (component.audio) {
        component.audio.mute(isSoundOff);
      }
    });
  }

  notifyTap() {
    triggerEvent('AppPointerTap');
  }

  setMasterTicker() {
    if (this.masterTicker === 'pixi') {
      // Use PixiJS ticker as master ticker
      Ticker.shared.autoStart = true;
      gsap.ticker.remove(gsap.updateRoot);
      gsap.ticker.sleep();
      gsap.ticker.lagSmoothing(0);
      gsap.ticker.wake = () => {};
      let gsapTime = gsap.globalTimeline.time();
      // Use 63 as a practical 60 FPS cap to avoid Pixi skipping near-16.67ms frames.
      // If we set 60, Pixi will render ~57.6-59.9 FPS
      this.app.ticker.maxFPS = 63;
      this.app.ticker.add(() => {
        gsapTime += this.app.ticker.deltaMS / 1000;
        gsap.updateRoot(gsapTime);
      }, null, UPDATE_PRIORITY.HIGH);
    } else {
      // Use legacy settings
      Ticker.shared.maxFPS = 60;
    }
  }
}
